import { Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { GridContextMenuItem, WcGridComponent } from '../../components/wc-grid/wc-grid.component';
import { OpenGrid } from '../../components/wc-grid/wc-grid-popup/wc-grid-popup.component';
import { ErrorDetailDialogComponent } from '../../utility/error-detail-dialog/error-detail-dialog.component';
import { EventPayloadDialogComponent } from '../../utility/event-payload-dialog/event-payload-dialog.component';
import { EditDialogService } from '../../utility/dialog/edit-dialog-service';
import { HttpService } from '../../utility/http-service';

interface TaskRunnerClient {
  clientId: string;
  displayName: string;
}

interface QuickRunHistoryEntry {
  jobType: string;
  clientId: string | null;
  jobKey: string | null;
  timestamp: number;
}

const QUICK_RUN_HISTORY_KEY = 'wc-taskrunner-history';
const QUICK_RUN_HISTORY_MAX = 10;

@Component({
  selector: 'app-version-management',
  standalone: false,
  templateUrl: './version-management.component.html',
  styleUrl: './version-management.component.css'
})
export class VersionManagementComponent implements OnInit {

  @ViewChild('summaryGrid') summaryGrid?: WcGridComponent;
  @ViewChild('historyGrid') historyGrid?: WcGridComponent;

  readonly errorMenuItems: GridContextMenuItem[] = [
    {
      id: 'viewError',
      label: 'View Error Details',
      disabled: (row) => row['RunStatus'] !== 'Error'
    }
  ];

  readonly rerunMenuItem: GridContextMenuItem = { id: 'rerun', label: 'Rerun' };
  readonly viewEventsMenuItem: GridContextMenuItem = { id: 'viewEvents', label: 'View Events' };
  readonly viewPayloadMenuItem: GridContextMenuItem = { id: 'viewPayload', label: 'View Payload' };

  readonly summaryMenuItems: GridContextMenuItem[] = [this.rerunMenuItem];
  // "View Events" is History-only for now — Task Summary rows (v_TaskSummary) don't carry
  // a job-identifying field yet (see WCHRON-585 plan notes).
  readonly historyMenuItems: GridContextMenuItem[] = [...this.errorMenuItems, this.rerunMenuItem, this.viewEventsMenuItem];

  rerunStatus: string | null = null;
  rerunError = false;
  rerunQueuing = false;

  jobTypes: string[] = [];
  clients: TaskRunnerClient[] = [];

  selectedJobType: string | null = null;
  selectedClientId: string | null = null;
  jobKeyInput = '';

  queuing = false;
  statusMessage: string | null = null;
  statusError = false;

  history: QuickRunHistoryEntry[] = [];

  constructor(private dialog: MatDialog, private http: HttpService, private editDialogService: EditDialogService) {}

  ngOnInit(): void {
    this.http.get<string[]>('TaskRunner/GetJobTypes', (res: string[]) => {
      this.jobTypes = res ?? [];
    });
    this.http.get<any[]>('TaskRunner/GetClients', (res: any[]) => {
      this.clients = (res ?? [])
        .map(c => ({ clientId: c.clientId, displayName: c.displayName }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    });
    this.loadHistory();
  }

  // ── Quick Run history (localStorage) ────────────────────────────────────────

  private loadHistory(): void {
    try {
      const raw = localStorage.getItem(QUICK_RUN_HISTORY_KEY);
      this.history = raw ? (JSON.parse(raw) as QuickRunHistoryEntry[]) : [];
    } catch {
      this.history = [];
    }
  }

  private saveHistory(): void {
    localStorage.setItem(QUICK_RUN_HISTORY_KEY, JSON.stringify(this.history));
  }

  private pushHistory(entry: QuickRunHistoryEntry): void {
    // Dedupe by composite key: jobType + clientId + jobKey
    const isSame = (a: QuickRunHistoryEntry, b: QuickRunHistoryEntry) =>
      a.jobType === b.jobType && a.clientId === b.clientId && a.jobKey === b.jobKey;
    this.history = [entry, ...this.history.filter(h => !isSame(h, entry))]
      .slice(0, QUICK_RUN_HISTORY_MAX);
    this.saveHistory();
  }

  applyHistoryItem(entry: QuickRunHistoryEntry): void {
    this.selectedJobType = entry.jobType;
    this.selectedClientId = entry.clientId;
    this.jobKeyInput = entry.jobKey ?? '';
    this.statusMessage = null;
    this.statusError = false;
  }

  runHistoryItem(entry: QuickRunHistoryEntry, event: Event): void {
    event.stopPropagation();
    this.applyHistoryItem(entry);
    this.queueTask();
  }

  removeHistoryItem(idx: number, event: Event): void {
    event.stopPropagation();
    this.history = this.history.filter((_, i) => i !== idx);
    this.saveHistory();
  }

  clearHistory(): void {
    this.history = [];
    this.saveHistory();
  }

  clientDisplayName(clientId: string | null): string {
    if (!clientId) return '';
    return this.clients.find(c => c.clientId === clientId)?.displayName ?? clientId;
  }

  timeAgo(ts: number): string {
    const diff = Math.max(0, Date.now() - ts);
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  }

  onSummaryTabShown(): void {
    const grid = this.summaryGrid;
    if (grid) grid.reload(grid.page);
  }

  onHistoryTabShown(): void {
    const grid = this.historyGrid;
    if (grid) grid.reload(grid.page);
  }

  onErrorAction(event: { id: string; row: Record<string, any> }): void {
    if (event.id !== 'viewError') return;
    this.dialog.open(ErrorDetailDialogComponent, {
      data: {
        errorMessage: event.row['ErrorMessage'],
        taskName: event.row['Description']
      },
      minWidth: 500,
      maxWidth: 800,
    });
  }

  onGridAction(event: { id: string; row: Record<string, any> }): void {
    if (event.id === 'viewError') {
      this.onErrorAction(event);
    } else if (event.id === 'rerun') {
      this.rerunFromRow(event.row);
    } else if (event.id === 'viewEvents') {
      this.viewEventsForRow(event.row);
    }
  }

  viewEventsForRow(row: Record<string, any>): void {
    const jobId: string | null = row['IntegrationId'] ?? null;
    const jobExecutionId: string | null = row['Id'] ?? null;
    if (!jobId && !jobExecutionId) return;

    OpenGrid('taskEventHistory', this.editDialogService, 1400, 700, false, {
      context: { jobId, jobExecutionId },
      contextMenuItems: [this.viewPayloadMenuItem],
      onAction: (id, evRow) => {
        if (id === 'viewPayload') this.openPayloadDialog(evRow['Payload'] ?? null);
      }
    });
  }

  openPayloadDialog(payload: string | null): void {
    this.dialog.open(EventPayloadDialogComponent, {
      data: { payload },
      minWidth: 500,
      maxWidth: 800,
    });
  }

  rerunFromRow(row: Record<string, any>): void {
    if (this.rerunQueuing) return;

    const integrationId: string | null = row['IntegrationId'] ?? null;
    const description: string | null = row['Description'] ?? null;
    if (!integrationId && !description) return;

    this.rerunQueuing = true;
    this.rerunStatus = null;
    this.rerunError = false;

    const label: string = description ?? integrationId!;
    const [endpoint, body] = integrationId
      ? ['TaskRunner/RerunJob', { integrationId }]
      : ['TaskRunner/RerunByDescription', { description }];

    this.http.postReturn<{ queued: boolean; jobType: string }>(endpoint,
      (res: { queued: boolean; jobType: string }) => {
        this.rerunQueuing = false;
        this.rerunError = false;
        this.rerunStatus = `Queued "${res.jobType ?? label}".`;
        this.pushHistory({
          jobType: res.jobType ?? label,
          clientId: null,
          jobKey: null,
          timestamp: Date.now(),
        });
      },
      undefined,
      body,
      () => {
        this.rerunQueuing = false;
        this.rerunError = true;
        this.rerunStatus = `Failed to queue "${label}". See error dialog for details.`;
      });
  }

  queueTask(): void {
    if (!this.selectedJobType || this.queuing) return;
    this.queuing = true;
    this.statusMessage = null;
    this.statusError = false;

    const body = {
      jobType: this.selectedJobType,
      clientId: this.selectedClientId,
      jobKey: this.jobKeyInput?.trim() || null
    };

    this.http.postReturn<{ queued: boolean }>('TaskRunner/QueueTask',
      (_res: { queued: boolean }) => {
        this.queuing = false;
        this.statusError = false;
        const label = this.selectedJobType;
        const client = this.selectedClientId ? ` for ${this.selectedClientId}` : '';
        this.statusMessage = `Queued "${label}"${client}.`;
        this.pushHistory({
          jobType: body.jobType!,
          clientId: body.clientId,
          jobKey: body.jobKey,
          timestamp: Date.now(),
        });
      },
      undefined,
      body,
      () => {
        this.queuing = false;
        this.statusError = true;
        this.statusMessage = 'Failed to queue task. See error dialog for details.';
      });
  }
}
